import React, { createContext, useContext, useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import axiosInstance from "../routes/axiosInstance";

// Create Translation Context
const TranslationContext = createContext();

// Translation Provider Component
export const TranslationProvider = ({ children, currentMember = null }) => {
  const toast = useToast();
  const [isTranslating, setIsTranslating] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [targetLanguage, setTargetLanguage] = useState("auto");

  // Get auth headers supporting both authenticated users and guest users
  const getAuthHeaders = useCallback(() => {
    const headers = {
      "Content-Type": "application/json",
    };

    // Check for guest user
    if (currentMember?.guestAccessId) {
      headers.Authorization = `Guest ${currentMember.guestAccessId}`;
    }
    // For now, don't handle authenticated users in this simple version

    return { headers };
  }, [currentMember]);

  // Translate text
  const translateText = useCallback(
    async (text, targetLang = targetLanguage, sourceLang = "auto") => {
      if (!text || !targetLang || targetLang === "auto") {
        return {
          originalText: text,
          translatedText: text,
          isTranslated: false,
        };
      }

      setIsTranslating(true);
      try {
        const config = getAuthHeaders();
        const response = await axiosInstance.post(
          "/api/translate",
          {
            text,
            targetLanguage: targetLang,
            sourceLanguage: sourceLang,
          },
          config,
        );

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || "Translation failed");
        }
      } catch (error) {
        console.error("Translation error:", error);
        toast({
          title: "Translation Error",
          description:
            error.response?.data?.message || "Failed to translate message",
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        // Return original text if translation fails
        return {
          originalText: text,
          translatedText: text,
          isTranslated: false,
          error: error.message,
        };
      } finally {
        setIsTranslating(false);
      }
    },
    [targetLanguage, getAuthHeaders, toast],
  );

  // Get supported languages
  const getSupportedLanguages = useCallback(async () => {
    try {
      // Languages endpoint doesn't require authentication
      const response = await axiosInstance.get("/api/translate/languages");

      if (response.data.success) {
        setSupportedLanguages(response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to get languages");
      }
    } catch (error) {
      console.error("Get languages error:", error);
      toast({
        title: "Error",
        description: "Failed to load supported languages",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return {};
    }
  }, [toast]);

  // Get language name from code
  const getLanguageName = useCallback(
    (code) => {
      return supportedLanguages[code] || code.toUpperCase();
    },
    [supportedLanguages],
  );

  const value = {
    translateText,
    getSupportedLanguages,
    getLanguageName,
    isTranslating,
    supportedLanguages,
    targetLanguage,
    setTargetLanguage,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use translation context
export const useTranslationContext = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error(
      "useTranslationContext must be used within a TranslationProvider",
    );
  }
  return context;
};

export default TranslationContext;
